import {
  CUSTOMER_CHAT_AUTHOR_CHANNEL,
  ICustomerChatComment,
  ICustomerChatCommentApiResponse
} from 'types/customerChat.types';
import {
  CUSTOMER_CHAT_ALLOWED_ORGANIZATION_IDS,
  isDevelopment,
  isLocalHost
} from 'constants/constants';
import { formatShortDateAndHour } from 'utils/formatDatesAndHours';

export const isCustomerChatEnabledForOrganization = (organizationId?: number | null): boolean => {
  if (isDevelopment || isLocalHost) return true;
  if (organizationId == null) return false;
  return CUSTOMER_CHAT_ALLOWED_ORGANIZATION_IDS.includes(organizationId);
};

const isCustomerChatAuthorChannel = (
  value: string | null | undefined
): value is CUSTOMER_CHAT_AUTHOR_CHANNEL =>
  value === CUSTOMER_CHAT_AUTHOR_CHANNEL.PARTNER || value === CUSTOMER_CHAT_AUTHOR_CHANNEL.OPERATIONS;

export const normalizeCustomerChatComment = (
  comment: ICustomerChatCommentApiResponse
): ICustomerChatComment => ({
  id: comment.id,
  comments: comment.comments,
  createdAt: comment.createdAt,
  user: comment.user ?? null,
  userId: comment.userId,
  authorChannel: isCustomerChatAuthorChannel(comment.authorChannel) ? comment.authorChannel : null,
  mentionsData: comment.mentionsData,
  mentions: null,
  image: comment.image ?? undefined,
  kamUserId: comment.kamUserId
});

export const normalizeCustomerChatComments = (
  comments: ICustomerChatCommentApiResponse[]
): ICustomerChatComment[] => comments.map(normalizeCustomerChatComment);

export const normalizeCustomerChatPersonLabel = (value: string): string =>
  value.trim().replace(/\s+/g, ' ').toLowerCase();

export const formatCustomerChatDateTime = (createdAt?: string | null): string => {
  if (!createdAt) return '';
  const { textWithTimezone } = formatShortDateAndHour(createdAt);
  return textWithTimezone ?? '';
};

export const getCustomerChatCommentAuthorUserId = (comment: ICustomerChatComment): number | undefined => {
  const { userId } = comment;
  return userId != null ? userId : undefined;
};

export const getCustomerChatCommentAuthorChannel = (
  comment: ICustomerChatComment
): CUSTOMER_CHAT_AUTHOR_CHANNEL | undefined => comment.authorChannel ?? undefined;

export const isBackofficeOperationsOutgoingMessage = (
  comment: ICustomerChatComment,
  viewerUserId: number | undefined,
  viewerDisplayNameNorm: string
): boolean => {
  const channel = getCustomerChatCommentAuthorChannel(comment);

  if (channel === CUSTOMER_CHAT_AUTHOR_CHANNEL.OPERATIONS) {
    return true;
  }

  if (channel === CUSTOMER_CHAT_AUTHOR_CHANNEL.PARTNER) {
    return false;
  }

  const authorId = getCustomerChatCommentAuthorUserId(comment);

  if (viewerUserId != null && authorId != null && authorId === viewerUserId) {
    return true;
  }

  if (authorId == null && viewerDisplayNameNorm.length > 0) {
    const authorLabel = normalizeCustomerChatPersonLabel(comment.user ?? '');
    if (authorLabel.length > 0 && authorLabel === viewerDisplayNameNorm) {
      return true;
    }
  }

  return false;
};
